// Contrato de las piezas COMPARTIDAS de filtros (context/plan_build_avivador_v2.md,
// Ajuste #16; context/home_filtros_panel/sintesis.md §2). Fase 0 de T3b
// (0.19.0-beta.3): lo escribe el orquestador y lo leen los ejecutores en
// paralelo. El Avivador es el primer consumidor; la home migra después, en una
// release propia, con ESTAS mismas piezas.
//
// Regla de la carpeta: NADA específico del Avivador ni de la home acá adentro
// (ni ramas, ni Control RDT, ni «redes»/«obras»). Los textos del dominio
// (títulos de sección, unidades, conteo del pie, aria de los chips) los pasa
// cada página. Sin JSX (regla de Fast Refresh): los componentes viven en sus
// .tsx y los helpers puros en facetas.ts, tokens.ts y atajos.ts.
//
// Archivos de la carpeta (dueño F1 salvo este):
// - boton-filtros.tsx     BotonFiltros
// - filtros-popover.tsx   FiltrosPopover + FiltroSeccion
// - grupo-chips.tsx       GrupoChips + ChipOpcion
// - chip-activo.tsx       ChipActivo
// - meta-filtros.tsx      MetaFiltros
// - facetas.ts            contarFacetas, alternarValor, textoConteo, conteoSr (+ facetas.test.ts)
// - tokens.ts             detectarToken, normalizarBusqueda (+ tokens.test.ts)
// - atajos.ts             esAtajoBuscar, accionEscBuscador (+ atajos.test.ts)
// Si hace falta un contexto (id del título de la sección para aria-labelledby),
// va en un .ts propio de la carpeta (p. ej. seccion-contexto.ts).
import type { ComponentPropsWithRef, ReactNode, RefObject } from 'react';

// ---------------------------------------------------------------------------
// Opciones de un grupo del popover (GrupoChips / ChipOpcion)
// ---------------------------------------------------------------------------

/** Sustantivo del conteo para lectores de pantalla: «Sin entregar, 21 de 41
 *  redes» (Avivador: `{ singular: 'rede', plural: 'redes' }`; home: obras). */
export interface UnidadConteo {
  singular: string;
  plural: string;
}

/** Una opción de un grupo del popover. */
export interface OpcionFiltro<V extends string = string> {
  /** Valor que se elige (código de rama, código de Control RDT, 'vivas', 'dias'…). */
  valor: V;
  /** Texto visible («Sin entregar», «Todo el equipo», «Más demoradas primero»). */
  etiqueta: string;
  /** Conteo facetado visible. Omitido = sin conteo (Tipo, Orden). */
  conteo?: number;
  /** Total de la opción sin el filtro que la recorta: se muestra «conteo/total»
   *  SOLO si `total > conteo` (Ramas «21/41», §13 2.2); si no, solo el conteo. */
  total?: number;
  /** Variante apagada: `text-tertiary`, SIN bajar la opacidad (AA; síntesis
   *  §2). Omitido = `conteo === 0`. Una opción ELEGIDA nunca se pinta apagada.
   *  Apagada no es deshabilitada: se puede elegir igual. */
  apagada?: boolean;
  /** Sin uso hoy: `GrupoChips`/`ChipOpcion` no lo leen (R2, migración a ReUI —
   *  «Mostrar» y «Orden» pasaron a `TarjetasRadio`, cuyo `OpcionTarjeta.descripcion`
   *  es el que hoy dibuja el texto secundario). Se deja en el contrato porque la
   *  home todavía puede necesitarlo para una faceta propia; no se borra. */
  descripcion?: string;
}

/** Forma visual de un grupo de selección única. Único valor: 'chips'
 *  (pastillas `rounded-full` h-7; Control RDT). Las variantes 'segmentado' y
 *  'lista' (Mostrar/Orden del Avivador) se fueron a `TarjetasRadio`
 *  (`@/components/comunes/tarjetas-radio`, R0) con la migración a ReUI: ahí
 *  vive hoy «elegir un valor» con conteo o descripción.
 *  Los grupos de selección múltiple son siempre 'chips'. */
export type VarianteGrupo = 'chips';

interface GrupoChipsBase<V extends string> {
  opciones: readonly OpcionFiltro<V>[];
  /** Id del título que nombra al grupo. Omitido = el título de la
   *  `FiltroSeccion` que lo contiene (la sección lo provee por contexto). */
  ariaLabelledby?: string;
  /** Sustantivo del conteo sr-only (ver `conteoSr`). Sin esto, solo el número. */
  unidad?: UnidadConteo;
}

/** Selección múltiple sobre `ToggleGroup multiple` (ui/toggle-group, Base UI):
 *  cada chip es un Toggle con `aria-pressed`, tilde si está elegido, flechas
 *  para moverse. `[]` = ninguna elegida = todas (regla de la síntesis). */
export interface GrupoChipsMultipleProps<V extends string = string> extends GrupoChipsBase<V> {
  modo: 'multiple';
  valor: readonly V[];
  /** Devuelve la selección nueva en el ORDEN DE `opciones` (no en el orden de clic). */
  onValorChange: (valor: V[]) => void;
}

/** Selección única sobre `RadioGroup` (ui/radio-group, Base UI): radios reales
 *  con flechas. `null` = ninguna marcada (el Orden cuando la Lista ordena por
 *  Rede, Estado o Control RDT: el radio del popover queda sin selección). */
export interface GrupoChipsUnicoProps<V extends string = string> extends GrupoChipsBase<V> {
  modo: 'unico';
  valor: V | null;
  onValorChange: (valor: V) => void;
  /** Default 'chips'. */
  variante?: VarianteGrupo;
}

export type GrupoChipsProps<V extends string = string> = GrupoChipsMultipleProps<V> | GrupoChipsUnicoProps<V>;

/** Una opción ya resuelta por `GrupoChips`. Visual:
 *  - elegida: `bg-primary/11 text-salta` (+ tilde en 'chips');
 *  - apagada (y no elegida): `text-tertiary`, sin opacidad;
 *  - conteo visible `aria-hidden` + `conteoSr` en `sr-only`;
 *  - 44 px en táctil (`[@media(hover:none)]:min-h-11`), `:active` translate-y-px
 *    con `motion-safe`, transiciones de 120 ms. */
export interface ChipOpcionProps<V extends string = string> {
  opcion: OpcionFiltro<V>;
  modo: 'multiple' | 'unico';
  variante: VarianteGrupo;
  seleccionada: boolean;
  unidad?: UnidadConteo;
}

// ---------------------------------------------------------------------------
// Popover «Filtros» (BotonFiltros, FiltrosPopover, FiltroSeccion)
// ---------------------------------------------------------------------------

/** Trigger del popover: ícono de sliders + «Filtros» + badge (Badge de ReUI,
 *  `bg-primary/12 text-salta` 10,5 px/650; solo si `nActivos > 0`) + chevron.
 *  h-9 (44 en táctil). Abierto (`data-popup-open`): `border-primary/55` + ring
 *  de 3 px `primary/12`. Nombre accesible: «Filtros» o «Filtros, N activos».
 *  `aria-haspopup="dialog"`. Es un `<button>` que reenvía props y ref (React 19:
 *  `ref` como prop) para usarse como `render` de `PopoverTrigger`. */
export interface BotonFiltrosProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  /** Número del badge. 0 = sin badge. */
  nActivos: number;
  /** Default «Filtros». */
  etiqueta?: string;
}

/**
 * El popover entero, CON su trigger (`BotonFiltros`) adentro: la página no arma
 * el `Popover`. Receta (relevamiento de la página §2; síntesis §2):
 * - `PopoverContent align="end"`, 400 px (`max-w` = 100vw − 24 px; verificación
 *   T3b §4.3: entra sin scroll a 1440x694), radio 12, `role="dialog"` con
 *   `aria-label={ariaLabel}`.
 * - Cabecera: «Filtros» + link «Limpiar» a la derecha (`aria-disabled` con
 *   `puedeLimpiar=false`). Al lado de «Filtros», si `nBusqueda > 0`, «+ N de la
 *   búsqueda» (text-tertiary): los tokens del buscador cuentan en el badge y
 *   no tienen control adentro (Ajuste #16).
 * - Cuerpo: `children` (las `FiltroSeccion`), con SCROLL INTERNO y
 *   `max-h` calculado con `--available-height` (Base UI) para que el pie quede
 *   fijo a la vista en tablet (1024×768).
 * - Pie fijo: `pie` a la izquierda (12 px, tertiary, SIN región viva) y el
 *   botón primario «Listo», que cierra.
 * - Foco: `initialFocus` en su forma función → con teclado, el primer control
 *   del primer grupo; con toque/mouse, el popup (así un Enter por reflejo no
 *   cae en «Limpiar»). Al cerrar, Base UI devuelve el foco al botón.
 * - Motion: entrada de 140 ms; nada con reduced-motion.
 */
export interface FiltrosPopoverProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** Badge del botón (la página cuenta: en el Avivador, `contarFiltros`). */
  nActivos: number;
  /** aria-label del diálogo: «Filtros del Avivador» / «Filtros de obras». */
  ariaLabel: string;
  puedeLimpiar: boolean;
  onLimpiar: () => void;
  /** Tokens del buscador activos (la línea «+ N de la búsqueda»). 0 = sin línea. */
  nBusqueda: number;
  /** Pie izquierdo, ya armado por la página: «<b>72</b> redes con estos filtros». */
  pie: ReactNode;
  /** Las `FiltroSeccion`. */
  children: ReactNode;
  /** Props extra del botón (className, aria-keyshortcuts…). `nActivos` sale de arriba. */
  botonProps?: Omit<BotonFiltrosProps, 'nActivos'>;
}

/** Una sección del popover: título en versalitas 10,5 px/650 tertiary, con un
 *  id propio (useId) que los `GrupoChips` de adentro usan como
 *  `aria-labelledby` por contexto. `separada` dibuja el separador ARRIBA de la
 *  sección (el que va antes de «Orden» en el Avivador). */
export interface FiltroSeccionProps {
  titulo: string;
  separada?: boolean;
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Chips de filtros activos y meta-row (ChipActivo, MetaFiltros)
// ---------------------------------------------------------------------------

/** Un filtro activo, ya armado por la página (en el Avivador, `chipsActivos`;
 *  `ChipActivo` de avivador-tipos.ts es `ChipFiltro<ChipKey>`). */
export interface ChipFiltro<K extends string = string> {
  /** Estable e único entre los chips visibles (la `key` de React). */
  key: K;
  /** Categoría, SIN los dos puntos (los pone el chip, en `<b>`): «Obra»,
   *  «Cliente», «Rama», «Control RDT», «Estado de obra» (nunca «Estado» a
   *  secas, Ajuste #16), «Tipo», «Orden». null = se lee solo (texto libre, Mostrar). */
  cat: string | null;
  label: string;
  /** Nombre accesible del botón entero (lo arma la página): «Quitar rama Sin entregar». */
  ariaLabel: string;
}

/** Atributo que `MetaFiltros` pone en cada chip (`data-filtro-chip={key}`):
 *  lo usan la regla de foco y el harness. Reemplaza a `data-avivador-chip` de T3a. */
export const ATTR_CHIP_FILTRO = 'data-filtro-chip';

/** El chip de la meta-row: el chip ENTERO es un `<button>` que quita el
 *  filtro (`aria-label={chip.ariaLabel}`), pastilla `bg-primary/11 text-salta`
 *  12 px/550, padding 3/6/3/10, categoría en `<b>` 650 + «:», X de lucide de
 *  12 px `aria-hidden`, `max-w-full` con el texto truncado, 44 px en táctil.
 *  Reemplaza a `FilterChip` (obras-filters.tsx) y `TokenChip` (v1) cuando la
 *  home migre. */
export interface ChipActivoProps<K extends string = string> {
  chip: ChipFiltro<K>;
  onQuitar: (key: K) => void;
}

/**
 * Meta-row (receta obras-filters.tsx; relevamiento de la página §3 y §4):
 * flex-wrap gap 8, 13 px muted, `mb-4`, `min-h-[26px]` y `min-h-11` FIJO en
 * táctil (la fila no salta cuando aparece el primer chip).
 * - `conteo` va envuelto en la ÚNICA región viva de la fila (`aria-live="polite"`
 *   `aria-atomic="true"`).
 * - Chips en `<ul aria-label={etiquetaChips}>`, un `<li>` por chip, en el orden
 *   recibido; «Limpiar todo» (link) al final, solo con 2 o más chips.
 * - `derecha` en un contenedor `ml-auto`.
 * - REGLA DE FOCO (vive acá, no en la página): al quitar un chip DESDE ESTA
 *   FILA, después del render el foco va al chip que quedó en esa posición; si
 *   era el último, al anterior; si no queda ninguno, a `focoSinChipsRef`. Con
 *   «Limpiar todo», a `focoSinChipsRef`. Si los chips cambian por otro lado
 *   (popover, Enter del buscador), el foco no se toca.
 */
export interface MetaFiltrosProps<K extends string = string> {
  /** «Mostrando <b>72</b> vivas de <b>192</b> redes»: lo arma la página. */
  conteo: ReactNode;
  chips: readonly ChipFiltro<K>[];
  onQuitarChip: (key: K) => void;
  onLimpiarTodo: () => void;
  /** Destino del foco cuando no quedan chips o tras «Limpiar todo» (el buscador). */
  focoSinChipsRef: RefObject<HTMLElement | null>;
  /** Slot derecho (Avivador: «N pospuestas», «Umbrales», «?»). */
  derecha?: ReactNode;
  /** aria-label de la lista de chips. Default «Filtros activos». */
  etiquetaChips?: string;
}

// ---------------------------------------------------------------------------
// Facetas (facetas.ts)
// ---------------------------------------------------------------------------

/**
 * Una dimensión filtrable para el conteo facetado: «cada filtro cuenta contra
 * todos los demás, nunca contra sí mismo» (§13 2.2/2.3; síntesis §2).
 */
export interface DimensionFaceta<I> {
  /** Id de la dimensión en el resultado ('ramas', 'controlRdt', 'estadoObra'…). */
  id: string;
  /** ¿El ítem pasa el filtro ACTIVO de esta dimensión? Sin filtro → siempre true. */
  pasa: (item: I) => boolean;
  /** Valores del ítem en esta dimensión, donde suma 1 cada uno. Varios si la
   *  dimensión lo pide (p. ej. 'todas' y 'vivas'); [] = no suma en ninguno. */
  valores: (item: I) => readonly string[];
}

/** `resultado[dim.id][valor]` = ítems que pasan TODAS las otras dimensiones y
 *  tienen ese valor. Solo aparecen los valores con al menos 1 (el que llama
 *  completa con `?? 0`). Toda dimensión de la entrada tiene su clave (aunque
 *  quede `{}`). */
export type ConteoFacetas = Record<string, Record<string, number>>;

// ---------------------------------------------------------------------------
// Detección de tokens parametrizada (tokens.ts)
// ---------------------------------------------------------------------------

/** Un valor elegible de una categoría. */
export interface CandidatoToken {
  /** Lo que se guarda en el filtro (el cc, el código de Control RDT, el cliente crudo, 'R3a'). */
  valor: string;
  /** Lo que se ve y contra lo que se busca («1426», «Ana Vera», «SOFSE», «Sin entregar»). */
  etiqueta: string;
  /** Otras formas que valen como coincidencia EXACTA de segundo nivel: el cc
   *  sin « UTE» («1408» para «1408 UTE»). Nunca cuentan para el parcial. */
  alias?: readonly string[];
}

/**
 * Una categoría del Enter, en el orden en que se prueba. `C` es la clave
 * propia de cada página (en el Avivador: 'obraCc' | 'cliente' | 'controlRdt' |
 * 'rama' | 'estadoObra' | 'obraNombre').
 */
export interface CategoriaToken<I, C extends string = string> {
  clave: C;
  /** Categoría legible («Obra», «Cliente»…), para anunciar lo detectado. */
  etiqueta: string;
  /** Los candidatos presentes en `items` (o fijos: las ramas, las personas).
   *  `detectarToken` los deduplica por `valor` (gana el primero). */
  candidatos: (items: readonly I[]) => readonly CandidatoToken[];
  /** 'exacta': solo niveles 1 y 2 (el CC). Default 'exacta-o-parcial'. */
  coincidencia?: 'exacta' | 'exacta-o-parcial';
}

/** Lo que devuelve `detectarToken` cuando una categoría matchea. */
export interface TokenDetectado<C extends string = string> {
  categoria: C;
  /** `CandidatoToken.valor` del elegido. */
  valor: string;
  /** `CandidatoToken.etiqueta` del elegido. */
  etiqueta: string;
}

// ---------------------------------------------------------------------------
// Atajos compartidos (atajos.ts)
// ---------------------------------------------------------------------------

/** Lo mínimo de un KeyboardEvent (un KeyboardEvent real lo cumple). */
export interface TeclaBasica {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  isComposing?: boolean;
}

/** Qué hacer con Esc en un buscador: borrar el texto, soltar el foco o nada. */
export type AccionEscBuscador = 'limpiar' | 'blur' | null;
